import React, { createContext, useContext, useState } from 'react';
import { CloudData } from '../../data/cloud-data.js';
import { Configuration } from '../../data/configuration.js';
import { VehicleView } from '../vehicle-view.js';
import { SectionPanel, SectionItem, ItemProps } from '../view-content.js';
import { CameraController, CameraViews } from '../../camera-controller.js';
import SelectedSectionItem from '../ui/selected-section-item.tsx';
import { AccessoriesConfigurator } from '../../configurator/accessories-configurator.js';
import { Utilities } from '../../utilities/utilities.js';
import ViewContentRows from '../ui/view-content-rows.tsx';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface AccessoriesViewProps {
	onBackButtonClicked: () => void;
	onNextButtonClicked: () => void;
	cameraController: CameraController;
	accessoriesConfigurator: AccessoriesConfigurator;
}

interface AccessoriesViewContextType {
	handleStorageItemClicked: (id: string) => void;
	handleCoversItemClicked: (id: string) => void;
	handleEquipmentItemClicked: (id: string) => void;
	handleServicesItemClicked: (id: string) => void;
	handleAftermarketItemClicked: (id: string) => void;
	handleAudioItemClicked: (id:string) => void;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function AccessoriesView(props: AccessoriesViewProps) {

	// initial values.
	const initialStorageItemNames = () => getItemNames(Configuration.accessoryStorageOptions);
	const initialEquipmentItemNames = () => getItemNames(Configuration.accessoryEquipmentOptions);
	const initialCoversItemNames = () => getItemNames(Configuration.accessoryCoversOptions);
	const initialServicesItemNames = () => getItemNames(Configuration.accessoryServicesOptions);
	const initialAftermarketItemNames = () => getItemNames(Configuration.accessoryAftermarketOptions);
	const initialAudioItemId = Configuration.audioOption;
	const initialAudioItemName = getItemName(initialAudioItemId);

	// states.
	const [selectedStorageItemNames, setSelectedStorageItemNames] = useState(initialStorageItemNames);
	const [selectedEquipmentItemNames, setSelectedEquipmentItemNames] = useState(initialEquipmentItemNames);
	const [selectedCoversItemNames, setSelectedCoversItemNames] = useState(initialCoversItemNames);
	const [selectedServicesItemNames, setSelectedServicesItemNames] = useState(initialServicesItemNames);
	const [selectedAftermarketItemNames, setSelectedAftermarketItemNames] = useState(initialAftermarketItemNames);
	const [selectedAudioItemNames, setSelectedAudioItemNames] = useState(initialAudioItemName);
	
	// handlers.
	
	const handleStorageItemClicked = (id: string) => {

		// if the tunnel storage pocket is chosen, set the camera to the tunnel storage viewpoint.
		if (id === Configuration.tunnelStorageOption) {
			props.cameraController.changeCameraView(CameraViews.TunnelStorageView);
		}

		// update the configuration.
		Configuration.toggleAccessoryOption(Configuration.accessoryStorageOptions, id);

		// update the selected item name(s).
		setSelectedStorageItemNames(getItemNames(Configuration.accessoryStorageOptions));

		// select the tunnel storage option on the car.
		props.accessoriesConfigurator.selectStorageAccessory(id);
	};
	
	const handleCoversItemClicked = (id: string) => {

		// update the configuration.
		Configuration.toggleAccessoryOption(Configuration.accessoryCoversOptions, id);

		// update the selected item name(s) from the configuration.
		setSelectedCoversItemNames(getItemNames(Configuration.accessoryCoversOptions));
	};

	const handleEquipmentItemClicked = (id: string) => {

		// if the sports exhaust items are chosen...
		if (id === Configuration.silverSportsExhaustOption || id === Configuration.blackSportsExhaustOption) {

			// for Supersport 400, ensure that at least one of the sports exhaust options is always selected, as silver is the default option.
			// on the standard model, both options can be de-selected to remove the sports exhaust entirely.
			if (Configuration.isSupersport400Model()) {
				const isSelected = Configuration.accessoryEquipmentOptions.has(id);
				const isSilverSelected = Configuration.accessoryEquipmentOptions.has(Configuration.silverSportsExhaustOption);
				const isBlackSelected = Configuration.accessoryEquipmentOptions.has(Configuration.blackSportsExhaustOption);
				const selectedCount = (isSilverSelected ? 1 : 0) + (isBlackSelected ? 1 : 0);
				if (isSelected && selectedCount === 1) {
					return;
				}
			}

			// if either of the sports exhaust items are chosen, de-select the other, as only one can be selected.
			if (id === Configuration.silverSportsExhaustOption) {
				Configuration.removeAccessoryOption(Configuration.accessoryEquipmentOptions, Configuration.blackSportsExhaustOption);
			}
			else if (id === Configuration.blackSportsExhaustOption) {
				Configuration.removeAccessoryOption(Configuration.accessoryEquipmentOptions, Configuration.silverSportsExhaustOption);
			}

			// set the camera to the exhaust viewpoint.
			props.cameraController.changeCameraView(CameraViews.ExhaustView);
		}

		// or, if the other equipment items are chosen...
		else {

			// if the cupholder or connectivity pack is chosen, set the camera to the cupholder viewpoint.
			if (id === Configuration.cupHolderOption || id === Configuration.connectivityPackOption) {
				props.cameraController.changeCameraView(CameraViews.CupHolderView);
			}
		}

		// update the configuration.
		Configuration.toggleAccessoryOption(Configuration.accessoryEquipmentOptions, id);

		// update the selected item name(s) from the configuration.
		setSelectedEquipmentItemNames(getItemNames(Configuration.accessoryEquipmentOptions));

		// select the equipment option on the car.
		props.accessoriesConfigurator.selectEquipmentAccessory(id);
	};

	const handleServicesItemClicked = (id: string) => {

		// update the configuration.
		Configuration.toggleAccessoryOption(Configuration.accessoryServicesOptions, id);

		// update the selected item name(s) from the configuration.
		setSelectedServicesItemNames(getItemNames(Configuration.accessoryServicesOptions));
	};

	const handleAftermarketItemClicked = (id: string) => {
		
		// prevent the pre-equipment pack option from being de-selected if a luggage rack item is selected.
		const luggageRackAccessories = CloudData.getLuggageRackAccessories();
		if (id === Configuration.preEquipmentPackOption && Configuration.accessoryAftermarketOptions.has(Configuration.preEquipmentPackOption)) {
			let hasLuggageRack = false;
			luggageRackAccessories.forEach((item) => {
				if (Configuration.accessoryAftermarketOptions.has(item)) {
					hasLuggageRack = true;
				}
			});
			if (hasLuggageRack) return;
		}

		// update the configuration.
		Configuration.toggleAccessoryOption(Configuration.accessoryAftermarketOptions, id);

		// if a luggage rack item is chosen...
		if (luggageRackAccessories.has(id)) {

			// set the camera to the luggage rack viewpoint.
			props.cameraController.changeCameraView(CameraViews.LuggageRackView);

			// deselect the other luggage rack items, as only one can be selected.
			luggageRackAccessories.forEach((item) => {
				if (item !== id) {
					Configuration.removeAccessoryOption(Configuration.accessoryAftermarketOptions, item);
				}
			});

			// select the pre-equipment pack option, as this is required for all luggage rack options.
			Configuration.addAccessoryOption(Configuration.accessoryAftermarketOptions, Configuration.preEquipmentPackOption);
			props.accessoriesConfigurator.selectAftermarketAccessory(id);

		}

		// update the selected item name(s) from the configuration.
		setSelectedAftermarketItemNames(getItemNames(Configuration.accessoryAftermarketOptions));

	};

	const handleAudioItemClicked = (id: string) => {

		// update the configuration.
		Configuration.audioOption = id;
		Configuration.updateCosts();

		// update the selected item name(s) from the configuration.
		setSelectedAudioItemNames(getItemName(id));

		props.accessoriesConfigurator.selectAudioAccessory(id);
	};

	// return the HTML.
	return (
		<AccessoriesViewContext.Provider value={{ handleStorageItemClicked, handleCoversItemClicked, handleEquipmentItemClicked, handleServicesItemClicked, handleAftermarketItemClicked, handleAudioItemClicked }}>
			<VehicleView
				onBackButtonClicked={props.onBackButtonClicked}
				onNextButtonClicked={props.onNextButtonClicked}>
				<div id="view-content">
					<div id="view-content-header">
						<h2 id="ga4-accessories">
							ACCESSORIES
						</h2>
					</div>

					<SectionPanel
						subHeading={'STORAGE'}
						shouldStartClosed={true}
						modalContent={CloudData.getAccessoryModalContent(Configuration.accessoryStorageOptions)}
					>
						<SelectedSectionItem
							itemName={selectedStorageItemNames}
							sectionPrice={CloudData.getAccessoryPrices(Configuration.accessoryStorageOptions, { ref: false })}
						/>
						<StorageItems />
					</SectionPanel>
					
					<SectionPanel
						subHeading={'COVERS'}
						shouldStartClosed={true}
						modalContent={CloudData.getAccessoryModalContent(Configuration.accessoryCoversOptions)}
					>
						<SelectedSectionItem
							itemName={selectedCoversItemNames}
							sectionPrice={CloudData.getAccessoryPrices(Configuration.accessoryCoversOptions, { ref: false })}
						/>
						<CoversItems />
					</SectionPanel>
					
					<SectionPanel
						subHeading={'EQUIPMENT'}
						shouldStartClosed={true}
						modalContent={CloudData.getAccessoryModalContent(Configuration.accessoryEquipmentOptions)}
					>
						<SelectedSectionItem
							itemName={selectedEquipmentItemNames}
							sectionPrice={CloudData.getAccessoryPrices(Configuration.accessoryEquipmentOptions, { ref: false })}
						/>
						<EquipmentItems />
					</SectionPanel>

					<SectionPanel
						subHeading={'SERVICES'}
						shouldStartClosed={true}
						modalContent={CloudData.getAccessoryModalContent(Configuration.accessoryServicesOptions)}
					>
						<SelectedSectionItem
							itemName={selectedServicesItemNames}
							sectionPrice={CloudData.getAccessoryPrices(Configuration.accessoryServicesOptions, { ref: false })}
						/>
						<ServicesItems />
					</SectionPanel>

					<SectionPanel
						subHeading={'AFTERMARKET'}
						shouldStartClosed={true}
						modalContent={CloudData.getAccessoryModalContent(Configuration.accessoryAftermarketOptions)}
					>
						<SelectedSectionItem
							itemName={selectedAftermarketItemNames}
							sectionPrice={CloudData.getAccessoryPrices(Configuration.accessoryAftermarketOptions, { ref: false })}
						/>
						<AftermarketItems />
					</SectionPanel>

					<SectionPanel
						subHeading={'AUDIO'}
						shouldStartClosed={true}
						modalContent={CloudData.getModalContent(Configuration.audioOption, CloudData.accessoryOptions)}
					>
						<SelectedSectionItem
							itemName={selectedAudioItemNames}
							sectionPrice={CloudData.getPrice(Configuration.audioOption, CloudData.accessoryOptions, { ref: false })}
						/>
						<AudioItems />
					</SectionPanel>
				</div>
			</VehicleView>
		</AccessoriesViewContext.Provider>
	);
}

//-----------------------------------------------------------------------------------------
// Private Components:
//-----------------------------------------------------------------------------------------

// context to allow any view in the hierarchy to access the selected item id and call the item clicked handler.
const AccessoriesViewContext = createContext<AccessoriesViewContextType | undefined>(undefined);

function StorageItems() {

	// get the context and ensure it is valid.
	const context = useContext(AccessoriesViewContext);
	if (!context) {
		throw new Error('AccessoriesViewContext must be used within a AccessoriesView component');
	}

	// return the HTML.
	return (
		<ViewContentRows>
			{!Configuration.isSupersportModelInUsRegion() && <StorageItem id={'SBAG011'} imageFilePath={'hood-storage-bag'} />}
			{!Configuration.isSupersportModelInUsRegion() && <StorageItem id={'SBAG001'} imageFilePath={'hardtop-storage-bag'} />}
			<StorageItem id={'SBAG014'} imageFilePath={'tunnel-storage-pocket'} />
		</ViewContentRows>
	);
}

function CoversItems() {

	// return the HTML.
	return (
		<ViewContentRows>
			<CoversItem id={'COV001'} imageFilePath={'indoor-car-cover'} />
			<CoversItem id={'COV002'} imageFilePath={'outdoor-car-cover'} />
		</ViewContentRows>
	);
}

function EquipmentItems() {
	return Configuration.isSupersport400Model() ? <EquipmentItems400/> : <EquipmentItemsStandard/>;
}

function EquipmentItemsStandard() {
	return (
		<ViewContentRows maxRowLength={1}>
			<div className="view-content-row">
				<EquipmentItem id={'E009'} imageFilePath={'sports-exhaust-silver'} />
				<EquipmentItem id={'E010'} imageFilePath={'sports-exhaust-black'} />
			</div>
			<div className="view-content-row">
				{!Configuration.isSupersportModelInUsRegion() && <EquipmentItem id={'E003'} imageFilePath={'battery-conditioner'} />}
				<EquipmentItem id={'E012'} imageFilePath={'dynamic-handling-pack'} />
				<EquipmentItem id={'E013'} imageFilePath={'limited-slip-differential'} />
				<EquipmentItem id={'SF001'} imageFilePath={'heated-seats'} />
				<EquipmentItem id={'SF004'} imageFilePath={'comfort-plus-seating'} />
				<EquipmentItem id={'CP012'} imageFilePath={'connectivity-pack'} />
				<EquipmentItem id={'SF005'} imageFilePath={'cup-holder'} />
			</div>
		</ViewContentRows>
	);
}

function EquipmentItems400() {
	return (
		<ViewContentRows maxRowLength={1}>
			<div className="view-content-row">
				<EquipmentItem id={'EX007'} imageFilePath={'sports-exhaust-silver'} />
				<EquipmentItem id={'EX008'} imageFilePath={'sports-exhaust-black'} />
			</div>
			<div className="view-content-row">
				<EquipmentItem id={'E003'} imageFilePath={'battery-conditioner'}/>
				<EquipmentItem id={'E013'} imageFilePath={'limited-slip-differential'} />
				<EquipmentItem id={'SF001'} imageFilePath={'heated-seats'} />
				<EquipmentItem id={'SF004'} imageFilePath={'comfort-plus-seating'} />
				<EquipmentItem id={'CP012'} imageFilePath={'connectivity-pack'} />
				<EquipmentItem id={'SF005'} imageFilePath={'cup-holder'} />
			</div>
		</ViewContentRows>
	);
}

function ServicesItems() {
	return (
		<ViewContentRows>
			{!Configuration.isSupersportModelInUsRegion() && <ServicesItem id={'E005'} imageFilePath={'cat-s5-tracker'} />}
			<ServicesItem id={'AM001'} imageFilePath={'photo-build-record'} />
			<ServicesItem id={'AM043'} imageFilePath={'extended-warranty'} />
		</ViewContentRows>
	);
}

function AftermarketItems() {
	
	// return the HTML.
	return (
		<ViewContentRows>
			<AftermarketItem id={'AM044'} imageFilePath={'luggage-rack-silver'} />
			<AftermarketItem id={'AM045'} imageFilePath={'luggage-rack-black'} />
			<AftermarketItem id={'AM032'} imageFilePath={'pre-equipment-pack'} />
		</ViewContentRows>
	);
}

function AudioItems() {

	// get the context and ensure it is valid.
	const context = useContext(AccessoriesViewContext);
	if (!context) {
		throw new Error('AccessoriesViewContext must be used within a AccessoriesView component');
	}

	// return the HTML.
	return (
		<ViewContentRows>
			<AudioItem id={'CP004'} imageFilePath={'bluetooth-audio'} />
			<AudioItem id={'CP010'} imageFilePath={'sennheiser-audio'} />
		</ViewContentRows>
	);
}

function StorageItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(AccessoriesViewContext);
	if (!context) {
		throw new Error('AccessoriesViewContext must be used within a AccessoriesView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/accessories/${props.imageFilePath}.svg`}
			isSelected={Configuration.accessoryStorageOptions.has(props.id)}
			onItemClicked={() => {
				if (props.clickable === false) return;
				context.handleStorageItemClicked(props.id);
			}}
			isDisabled={props.isDisabled}
			showName={true}
			itemPrice={CloudData.getPrice(props.id, CloudData.accessoryOptions, { ref: false })}
			shouldEnlarge={true}
		/>
	);
}

function CoversItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(AccessoriesViewContext);
	if (!context) {
		throw new Error('AccessoriesViewContext must be used within a AccessoriesView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/accessories/${props.imageFilePath}.svg`}
			isSelected={Configuration.accessoryCoversOptions.has(props.id)}
			isDisabled={props.isDisabled}
			onItemClicked={context.handleCoversItemClicked}
			showName={true}
			itemPrice={CloudData.getPrice(props.id, CloudData.accessoryOptions, { ref: false })}
			shouldEnlarge={true}
		/>
	);
}

function EquipmentItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(AccessoriesViewContext);
	if (!context) {
		throw new Error('AccessoriesViewContext must be used within a AccessoriesView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/accessories/${props.imageFilePath}.svg`}
			isSelected={Configuration.accessoryEquipmentOptions.has(props.id)}
			onItemClicked={context.handleEquipmentItemClicked}
			showName={true}
			itemPrice={CloudData.getPrice(props.id, CloudData.accessoryOptions, { ref: false })}
			shouldEnlarge={true}
		/>
	);
}

function ServicesItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(AccessoriesViewContext);
	if (!context) {
		throw new Error('AccessoriesViewContext must be used within a AccessoriesView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/accessories/${props.imageFilePath}.svg`}
			isSelected={Configuration.accessoryServicesOptions.has(props.id)}
			onItemClicked={context.handleServicesItemClicked}
			showName={true}
			itemPrice={CloudData.getPrice(props.id, CloudData.accessoryOptions, { ref: false })}
			shouldEnlarge={true}
		/>
	);
}

function AftermarketItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(AccessoriesViewContext);
	if (!context) {
		throw new Error('AccessoriesViewContext must be used within a AccessoriesView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/accessories/${props.imageFilePath}.svg`}
			isSelected={Configuration.accessoryAftermarketOptions.has(props.id)}
			isDisabled={props.isDisabled}
			onItemClicked={context.handleAftermarketItemClicked}
			showName={true}
			itemPrice={CloudData.getPrice(props.id, CloudData.accessoryOptions, { ref: false })}
			shouldEnlarge={true}
		/>
	);
}

function AudioItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(AccessoriesViewContext);
	if (!context) {
		throw new Error('AccessoriesViewContext must be used within a AccessoriesView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/accessories/${props.imageFilePath}.svg`}
			isSelected={Configuration.audioOption === props.id}
			isDisabled={props.isDisabled}
			onItemClicked={() => {
				if (props.clickable === false) return;
				context.handleAudioItemClicked(props.id);
			}}
			showName={true}
			itemPrice={CloudData.getPrice(props.id, CloudData.accessoryOptions, { ref: false })}
			shouldEnlarge={true}
		/>
	);
}

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

// returns a comma-separated list of names for the currently selected item(s) in the given accessory options with itemised prices.
function getItemNames(accessoryOptions: Set<string>): JSX.Element {
	const itemNames: JSX.Element[] = [];
	Array.from(accessoryOptions).forEach((item, index) => {
		const itemName = getItemName(item);
		const itemPrice = Utilities.asCurrency(CloudData.getPrice(item, CloudData.accessoryOptions, { ref: false }), Configuration.regionCode);
		const formattedPrice = itemPrice && itemPrice.endsWith('.00') ? itemPrice.slice(0, -3) : itemPrice;

		itemNames.push(
			<span key={item}>
				{index > 0 ? ', ' : ''}

				{itemName}
				{formattedPrice && (
					<span className="itemised-accessory-price"> ({formattedPrice})</span>
				)}
			</span>
		);
	});
	return (
		<React.Fragment>
			{itemNames.map(item => item)}
		</React.Fragment>
	);
}

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getItemName(id: string): string {
	return CloudData.getName(id, CloudData.accessoryOptions);
}
